import { Box } from '@atlaskit/primitives';
import { ConfluencePageAri } from '@atlassian/ari';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { loadContent } from '@databases/redux/connections/actions';
import { selectContentStatus } from '@databases/redux/connections/selectors';
import {
  Content,
  ResolvedContentReference,
  CreatePageReference,
} from '@databases/types/confluence';
import {
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO,
  DatabaseFieldType,
  PageSubType,
  ParentPageOption,
} from '@databases/types/database';
import {
  createPageWithOptions,
  resolveSinglePageLinkValue,
} from '@databases/utils/database/field-types/pageLink';
import { getDatabaseEntryOptional } from '@databases/utils/database/utils';
import { addContentToList } from '@databases/utils/redux/content/actions';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { getEntryPath } from '@databases/utils/urls';
import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { retryWithBackoff } from '@canvas/retry';

import { PopoverEdit } from '../PopoverEdit';
import { useDynamicParent, useFixedParentPage } from '../page-link/hooks';
import { EditComponentProps } from '../types';

import PageLinkSelect from './PageLinkSelect';
import { PageLinkValue } from './PageLinkValue';
import { Value } from './types';

const validate = (creationErrorIndex: number | null, currentIndex: number) => {
  return !(creationErrorIndex === currentIndex);
};

const PageLinkValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.PAGE_LINK>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  contentId,
  entryId,
  isSeamless,
  isDefaultValue,
  isFilterValueEdit,
}) => {
  const [pendingPages, setPendingPages] = useState<string[]>([]);
  const [creationErrorIndex, setCreationErrorIndex] = useState<number | null>(null);
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);

  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const client = useAnalytics();
  const { spaceKey } = useDatabaseContent();
  const { formatMessage } = useIntl();
  const isRenderedInSidePanel = isDefaultValue || isFilterValueEdit;

  const [database, entry, entryUrl] = defaultStoreUtils.useEqualSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, {
            contentId,
          })
        : undefined;
    const entry =
      database && entryId != null
        ? getDatabaseEntryOptional(database, entryId)
        : undefined;
    const baseUrl = selectBaseUrl(state);
    const entryUrl =
      database && entry ? baseUrl + getEntryPath(database, entry.id) : undefined;

    return [database, entry, entryUrl];
  });

  const noParentItem =
    field.settings.parentPageOption === ParentPageOption.FIXED &&
    !field.settings.parentPage &&
    !field.settings.isParentPageSourceDatabase;
  const creationErrorMessage = noParentItem
    ? formatMessage(
        messages['field-settings.type-page-link-create-inline-error.no-parent-item']
      )
    : undefined;

  const { isPending: isFixedPagePending, pageRef: fixedPageRef } = useFixedParentPage({
    contentId,
    field,
    entryId,
  });

  const { isPending: isDynamicParentPending, parentRef: dynamicParentPageRef } =
    useDynamicParent();

  // isParentPageSourceDatabase defaults to true so we need to add additional
  // checks before using the database as a parent page
  const resolvedIsParentPageSourceDatabase =
    field.settings.isParentPageSourceDatabase &&
    !field.settings.parentPage &&
    field.settings.creatable;

  const parentPageIsPending = resolvedIsParentPageSourceDatabase
    ? false
    : isFixedPagePending || isDynamicParentPending;

  const creationParentPage = useMemo(() => {
    if (field.settings.parentPageOption === ParentPageOption.DYNAMIC) {
      return dynamicParentPageRef;
    }
    if (resolvedIsParentPageSourceDatabase) {
      return {
        type: 'database',
        spaceKey,
        contentTitle: database?.name,
        contentId,
      } as ResolvedContentReference;
    }
    return fixedPageRef;
  }, [
    contentId,
    database?.name,
    dynamicParentPageRef,
    field.settings.parentPageOption,
    fixedPageRef,
    resolvedIsParentPageSourceDatabase,
    spaceKey,
  ]);

  const resolvedValues = defaultStoreUtils.useEqualSelector((state) =>
    value.map((v) => resolveSinglePageLinkValue(v, state))
  );

  const getAriForPageContentId = useCallback(
    (contentId: string) =>
      ConfluencePageAri.create({
        pageId: contentId,
        siteId: getCloudId() ?? '',
      }).toString(),
    []
  );

  const isMulti = resolvedValues.length > 1 || field.settings.multi;
  const isLiveDocEnabled = useBooleanFeatureFlag('databases-frontend-live-doc');

  const createNewPage = useCallback(
    async (value: Value, index: number) => {
      if ('type' in value && value.type === 'page' && value.create && entry && database) {
        const newPendingPages = [...pendingPages, value._id];
        setPendingPages(newPendingPages);

        const customTemplateIds = [
          CUSTOM_TEMPLATE_ENTRY_MACRO.templateId,
          CUSTOM_TEMPLATE_EMPTY.templateId,
        ];

        const finalTemplateId = customTemplateIds.includes(
          field.settings.pageTemplate.templateId
        )
          ? null
          : field.settings.pageTemplate.templateId;

        try {
          const spaceKey = value.spaceKey;

          const parentId = value.create.parentPageId as string;

          const newPageFromMutation = await createPageWithOptions({
            templateId: finalTemplateId,
            entryUrl: field.settings.displayEntryOnPage ? entryUrl ?? null : null,
            spaceKey,
            parentId,
            subType: isLiveDocEnabled ? field.settings.pageSubType : null,
            contentTitle: value.contentTitle,
          });

          if (!newPageFromMutation) {
            return undefined;
          }
          const newPage: Content = newPageFromMutation;

          client.sendCreatePageFromDatabaseEvent({
            contentId,
            newPageId: newPage.id,
            databaseContainer: getDatabaseContainerType(),
            parentId,
            pageLocation: field.settings.parentPageOption,
            templateId: finalTemplateId,
            hasEntryEmbed: field.settings.displayEntryOnPage ?? false,
            pageSubType: isLiveDocEnabled ? field.settings.pageSubType : PageSubType.PAGE,
          });

          // We need to wait for the content to be distributed across services and become available for datasources
          // In the future it can be replaced with a better content loading indicator and automated data refresh
          retryWithBackoff({
            callback: () => {
              return new Promise((resolve, reject) => {
                dispatch(
                  loadContent({
                    ari: getAriForPageContentId(newPage.id),
                    connectionId: field.id,
                    forceReload: true,
                  })
                );
                const contentStatus = selectContentStatus(
                  store.getState().connections,
                  getAriForPageContentId(newPage.id)
                );
                if (contentStatus?.status !== AsyncOperationStatus.SUCCEEDED) {
                  return reject(new Error('Content not loaded'));
                }

                resolve(true);
              });
            },
            shouldRetry: () => true,
            maxRetries: 10,
          });

          dispatch(addContentToList(newPage));
          return newPage;
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch {
          setCreationErrorIndex(index);
        } finally {
          setPendingPages((pendingPages) => pendingPages.filter((p) => p !== value._id));
        }
      }
    },
    [
      entry,
      database,
      pendingPages,
      field,
      entryUrl,
      isLiveDocEnabled,
      client,
      contentId,
      dispatch,
      getAriForPageContentId,
      store,
    ]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    setCreationErrorIndex(null);
  }, [onBlur]);

  const handleInputChange = useCallback(() => {
    setCreationErrorIndex(null);
  }, []);

  const getErrorMessage = useCallback(
    (i: number) => {
      if (!validate(creationErrorIndex, i)) {
        if (isLiveDocEnabled && field.settings.pageSubType === PageSubType.LIVE) {
          return formatMessage(
            messages['field-settings.type-page-link-live-doc-create-in-context-error']
          );
        } else {
          return formatMessage(
            messages['field-settings.type-page-link-create-in-context-error']
          );
        }
      }

      return '';
    },
    [creationErrorIndex, field.settings.pageSubType, formatMessage, isLiveDocEnabled]
  );

  const isCreateOption = (value: Omit<Value, '_id'>): value is CreatePageReference =>
    'create' in value;
  const CurrentDataSection = (
    <>
      {resolvedValues.map((pageLink) =>
        !isCreateOption(pageLink) ? (
          <PageLinkValue
            key={pageLink._id}
            resolvedValue={pageLink}
            resolvedValues={resolvedValues}
            onChange={onChange}
            autoFocus={autoFocus}
            addDataSectionRef={addDataSectionRef}
          />
        ) : null
      )}
    </>
  );

  const PageLinkSelectAddDataSection = (
    <Box ref={addDataSectionRef}>
      <ValidationStatus
        error={getErrorMessage(0)}
        isTouched={!validate(creationErrorIndex, 0)}
      >
        {(props) => (
          <PageLinkSelect
            {...props}
            onInputChange={handleInputChange}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            autoFocus={autoFocus}
            creationParentPage={creationParentPage}
            allowCreation={field.settings.creatable}
            creationErrorMessage={creationErrorMessage}
            isPending={parentPageIsPending}
            isSeamless={isSeamless}
            preserveInputValue={
              resolvedValues.length > 0
                ? !(
                    'contentTitle' in resolvedValues[0] ||
                    'contentId' in resolvedValues[0]
                  )
                : undefined
            }
            isLoading={pendingPages.length > 0}
            pageSubType={field.settings.pageSubType}
            isLiveDocEnabled={isLiveDocEnabled}
            resolvedValues={resolvedValues}
            createNewPage={createNewPage}
            getAriForPageContentId={getAriForPageContentId}
            isRenderedInSidePanel={isRenderedInSidePanel}
          />
        )}
      </ValidationStatus>
    </Box>
  );

  return (
    <PopoverEdit
      isMultiSelect={isMulti}
      hasCurrentData={value.length > 0}
      currentDataSection={CurrentDataSection}
      addDataSection={PageLinkSelectAddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
    />
  );
};

export default PageLinkValuePopoverEdit;
